import * as R from 'ramda';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
// utilities
import { sendRequest } from '../../utilities/http';
import endpointsMap from '../../utilities/endpoints';
//////////////////////////////////////////////////

export const getInitialDates = () => ({
  dateTo: G.getCurrentDay(),
  dateFrom: G.getStartOfMonthDay(),
});

export const loadInvoices = async function (props, driver) {
  const data = {
    invoiceGuids: driver.invoiceGuids,
    [GC.FIELD_BRANCH_GUID]: props.branchGuid,
  };
  const res = await sendRequest('post', endpointsMap.driverPayrollInvoices, { data });
  let driverState = driver;
  if (G.isResponseSuccess(res.status)) {
    const initialInvoicesProps = [{
      name: 'selected',
      value: G.isTrue(driver.selected),
    }];
    driverState = R.mergeRight(
      driverState,
      {
        invoices: R.indexBy(
          R.prop(GC.FIELD_GUID),
          G.setPropsWithValuesToArrayItems(
            res.data,
            initialInvoicesProps,
          ),
        ),
        invoicesLoading: false,
      },
    );
  } else {
    driverState = R.mergeRight(
      driverState,
      {
        invoicesError: true,
        invoicesLoading: false,
      },
    );
  }
  props.updateDrivers(R.assoc(driver.driverGuid, driverState, props.drivers));
};

export const loadVendorInvoices = async function (props, vendor) {
  const data = {
    invoiceGuids: vendor.invoiceGuids,
    [GC.FIELD_BRANCH_GUID]: props.branchGuid,
  };
  const res = await sendRequest('post', endpointsMap.vendorPayrollInvoices, { data });
  let vendorState = vendor;
  if (G.isResponseSuccess(res.status)) {
    const initialInvoicesProps = [{
      name: 'selected',
      value: G.isTrue(vendor.selected),
    }];
    vendorState = R.mergeRight(
      vendorState,
      {
        invoices: R.indexBy(
          R.prop(GC.FIELD_GUID),
          G.setPropsWithValuesToArrayItems(
            res.data,
            initialInvoicesProps,
          ),
        ),
        invoicesLoading: false,
      },
    );
  } else {
    vendorState = R.mergeRight(
      vendorState,
      {
        invoicesError: true,
        invoicesLoading: false,
      },
    );
  }
  props.updateVendors(R.assoc(R.prop(GC.FIELD_FLEET_VENDOR_GUID, vendor), vendorState, props.vendors));
};

export const getPayrollsOnAllTogglePayrollSelect = (drivers, checked, propName) => R.indexBy(
  R.prop(propName), R.map(
    (driver) => ({
      ...driver,
      'selected': R.not(checked),
    }),
    R.values(drivers),
));

export const getPayrollsOnTogglePayrollSelect = (driver, drivers, propName) => R.assocPath(
  [R.prop(propName, driver), 'selected'],
  R.not(driver.selected),
  drivers,
);

export const getDriverOnToggleInvoiceSelect = (driver, invoice) => {
  const driverData = R.assocPath(
    ['invoices', invoice.guid, 'selected'],
    R.not(invoice.selected),
    driver,
  );
  const payrollCurrency = R.pathOr(
    GC.DEFAULT_UI_CURRENCY,
    [GC.FIELD_CURRENCY],
    driverData,
  );

  return R.mergeRight(
    driverData,
    {
      allSelected: R.all(
        R.prop('selected'),
        R.values(driverData.invoices),
      ),
      invoicesTotal: R.sum(R.map(
        (invoice) => {
          const { total, currency, selected } = invoice;
          if (R.not(selected)) return 0;

          if (G.notEquals(currency, payrollCurrency)) {
            return G.convertCurrencyByExchangeRate(total, currency, payrollCurrency);
          }

          return total;
        },
        R.values(driverData.invoices),
      )),
    },
  );
};

export const getUpdatedInvoices = (driver, updatedInvoice) => R.map(
  (invoice) => {
    if (R.eqProps(GC.FIELD_GUID, invoice, updatedInvoice)) {
      return R.mergeRight(
        invoice,
        R.pick(
          [
            GC.FIELD_MODE,
            GC.FIELD_CURRENCY,
            GC.FIELD_INVOICE_DATE,
            GC.FIELD_CHARGE_TOTAL,
            GC.FIELD_INVOICE_STATUS,
            GC.FIELD_TOTAL_TRIP_WEIGHT,
            GC.FIELD_DEADHEAD_DISTANCE,
            GC.FIELD_TOTAL_TRIP_DISTANCE,
            GC.FIELD_DEADHEAD_DISTANCE_UOM,
            GC.FIELD_TOTAL_TRIP_WEIGHT_UOM,
            GC.FIELD_TOTAL_TRIP_DISTANCE_UOM,
          ],
          updatedInvoice,
        ),
      );
    }

    return invoice;
  },
  driver.invoices,
);

export const recalculateDriverInvoices = (drivers, updatedInvoice) => R.map(
  (driver) => {
    if (driver.expanded) {
      const invoices = getUpdatedInvoices(driver, updatedInvoice);
      const invoicesTotal = R.sum(R.map(
        (inv) => G.ifElse(
          inv.selected,
          inv.total,
          0,
        ),
        R.values(invoices),
      ));

      return R.mergeRight(driver, { invoices, invoicesTotal });
    }
    return driver;
  },
  drivers,
);

export const convertPayroll = (payroll) => (
  R.assoc(
    'invoiceGuids',
    G.ifElse(
      payroll.allSelected,
      payroll.invoiceGuids,
      R.map(
        (p) => p.guid,
        R.filter(
          (payroll) => payroll.selected,
          R.values(payroll.invoices),
        ),
      ),
    ),
    R.pick([
      GC.FIELD_DATE_TO,
      GC.FIELD_CURRENCY,
      GC.FIELD_DATE_FROM,
      GC.FIELD_DRIVER_GUID,
      GC.FIELD_PAYROLL_NUMBER,
    ], payroll),
  )
);

export const getPayrollsData = (payrolls) => (
  R.map(convertPayroll, payrolls)
);

export const convertVendorPayroll = (payroll) => (
  R.assoc(
    'invoiceGuids',
    G.ifElse(
      payroll.allSelected,
      payroll.invoiceGuids,
      R.map(
        (payroll) => G.getGuidFromObject(payroll),
        R.filter(
          (payroll) => payroll.selected,
          R.values(payroll.invoices),
        ),
      ),
    ),
    R.pick([
      GC.FIELD_CURRENCY,
      GC.FIELD_PAYROLL_NUMBER,
      GC.FIELD_PAYROLL_DATE_TO,
      GC.FIELD_PAYROLL_DATE_FROM,
      GC.FIELD_FLEET_VENDOR_GUID,
    ], payroll),
  )
);

export const getVendorPayrollsData = (payrolls) => (
  R.map(convertVendorPayroll, payrolls)
);

export const validateCreatePayroll = (payrolls, autogenerate) => (
  G.ifElse(
    autogenerate,
    true,
    R.all(
      (payroll) => G.isNotEmpty(payroll.payrollNumber),
      payrolls,
    ),
  )
);

export const getUpdatePayrollRequestData = (payroll) => (
  {
    [GC.FIELD_PAYROLL_GUID]: G.getGuidFromObject(payroll),
    invoiceGuids: R.map(
      (invoice) => G.getGuidFromObject(invoice),
      R.filter(
        (invoice) => invoice.selected,
        R.values(payroll.invoices),
      ),
    ),
  }
);

export const getExportPayrollReportFields = (filterParams) => filterParams.map((prop, i) => ({
  sequence: i,
  name: prop.value,
  displayName: prop.name,
}));

export const composeObjectPropsToString = (
  omitFields = [],
  withoutValues = [],
  joiner = '',
) => R.compose(
  R.toLower(),
  R.join(joiner),
  R.without(withoutValues),
  R.values(),
  R.omit(omitFields),
);

export const mapListObjectPropsToPropString = (filterPropName, composeProps) => (
    R.map(
  (item) => (
      R.assoc(
    filterPropName,
    composeObjectPropsToString(
      R.prop('omitFields', composeProps),
      R.prop('withoutValues', composeProps),
      R.prop('joiner', composeProps),
    )(R.values(item)),
    item,
  ))));

export const filterListOfObjectsByPropContains = (
  filterString,
  filterPropName,
) => R.filter(
  (item) => R.propSatisfies((prop) => R.includes(R.toLower(filterString), prop), filterPropName, item),
);

export const getInvoiceChargesFromResponse = (charges = []) => ({
  mainChargesTotal: G.getChargeTotalFromCharges(charges, GC.CHARGE_TYPE_MAIN),
  fuelChargesTotal: G.getChargeTotalFromCharges(charges, GC.CHARGE_TYPE_FUEL),
  additionalChargesTotal: G.getChargeTotalFromCharges(charges, GC.CHARGE_TYPE_ADDITIONAL),
});

const getChargesGuids = R.compose(
  R.map(G.getGuidFromObject),
  R.filter(({ removed }) => R.not(removed)),
);

const getExcludedChargesGuids = R.compose(
  R.map(G.getGuidFromObject),
  R.filter(({ selected, removed }) => R.and(R.not(selected), R.not(removed))),
);

export const getChargesData = (charges) => {
  const guids = getChargesGuids(charges);
  const excludedGuids = getExcludedChargesGuids(charges);
  return { guids, excludedGuids };
};

const getExcludedLineGuids = R.compose(
  R.map(G.getGuidFromObject),
  R.filter(({ selected }) => R.not(selected)),
  R.reduce((acc, fuelCard) => R.concat(acc, fuelCard.details), []),
  R.filter(({ removed, selected }) => R.and(selected, R.not(removed))),
);

export const getFuelCardsData = (fuelCards) => {
  const { guids, excludedGuids } = getChargesData(fuelCards);
  const excludedLineGuids = getExcludedLineGuids(fuelCards);
  return {
    guids,
    excludedGuids,
    excludedLineGuids,
  };
};

export const getDriverPayrollsData = (driverPayrolls) => ({
  guids: R.compose(
    R.map(G.getGuidFromObject),
    R.filter(R.prop('selected')),
  )(driverPayrolls),
});

// UI
export const getTabs = (activeTab) => [
  {
    withCount: R.equals(activeTab, 0),
    text: G.getWindowLocale('titles:driver-payrolls', 'Driver Payrolls'),
  },
  {
    withCount: R.equals(activeTab, 1),
    text: `${G.getWindowLocale('titles:pivot', 'Pivot')} ${
      G.getWindowLocale('titles:payrolls', 'Payrolls')}`,
  },
  {
    withCount: R.equals(activeTab, 2),
    text: `${G.getWindowLocale('titles:payroll', 'Payroll')} ${
      G.getWindowLocale('titles:charges', 'Charges')}`,
  },
  {
    withCount: R.equals(activeTab, 3),
    text: G.getWindowLocale('titles:vendor-payrolls', 'Vendor Payrolls'),
  },
  {
    withCount: R.equals(activeTab, 4),
    text: `${G.getWindowLocale('titles:pivot', 'Pivot')} ${
      G.getWindowLocale('titles:vendor-payrolls', 'Vendor Payrolls')}`,
  },
  {
    withCount: R.equals(activeTab, 5),
    text: `${G.getWindowLocale('titles:vendor-payroll', 'Vendor Payroll')} ${
      G.getWindowLocale('titles:charges', 'Charges')}`,
  },
];

export const getSubTotal = (arr = [], payrollCurrency) => R.compose(
  G.mathRoundNumber,
  R.sum,
  R.map(({ total, currency }) => G.convertCurrencyByExchangeRate(total, currency, payrollCurrency)),
)(arr);

const currencies = ['USD', 'CAD', 'MXN', 'EUR'];

const getInvoicesTotals = R.compose(
  R.map(({ currency, invoicesTotal }) => ({
    currency,
    total: invoicesTotal,
  })),
  R.filter(({ invoicesTotal }) => R.and(G.isNotNilAndNotEmpty(invoicesTotal), G.isNotZero(invoicesTotal))),
);

export const getInvoicesTotalsForVendorPayrollFiltering = (invoices) => R.compose(
  R.filter(({ total }) => G.isNotZero(total)),
  R.map((currency) => ({
    currency,
    total: R.compose(
      G.mathRoundNumber,
      R.sum,
      R.map(R.prop(GC.FIELD_TOTAL)),
      R.filter(R.propEq(currency, GC.FIELD_CURRENCY)),
    )(invoices),
  })),
)(currencies);

const getMainChargesTotals = R.compose(
  R.map(({ currency, mainChargesTotal }) => ({
    currency,
    total: mainChargesTotal,
  })),
  R.filter(({ mainChargesTotal }) => R.and(
    G.isNotZero(mainChargesTotal),
    G.isNotNilAndNotEmpty(mainChargesTotal),
  )),
);

export const getFuelCardsTotals = (fuelCards) => R.compose(
  R.filter(({ total }) => G.isNotZero(total)),
  R.map((currency) => ({
    currency,
    total: R.compose(
      G.mathRoundNumber,
      G.calculateFuelCardsTotal,
      R.filter(R.propEq(currency, GC.FIELD_CURRENCY)),
    )(fuelCards),
  })),
)(currencies);

export const getTollChargesTotals = (tollCharges) => R.compose(
  R.filter(({ total }) => G.isNotZero(total)),
  R.map((currency) => ({
    currency,
    [GC.FIELD_TOTAL]: R.compose(
      G.mathRoundNumber,
      G.calculateTollChargesTotal,
      R.filter(R.propEq(currency, GC.FIELD_CURRENCY)),
    )(tollCharges),
  })),
)(currencies);

const getDriverOrVendorDeductionTotals = (charges, payrollCurrency) => R.compose(
  R.filter(({ total }) => G.isNotZero(total)),
  R.map((currency) => ({
    currency,
    total: R.compose(
      G.mathRoundNumber,
      G.calculatePayrollDriverDeduction,
      R.filter((charge) => R.or(
        R.propEq(currency, GC.FIELD_CURRENCY, charge),
        R.and(R.equals(currency, payrollCurrency), G.isNilOrEmpty(R.prop(GC.FIELD_CURRENCY, charge))),
      )),
    )(charges),
  })),
)(currencies);

const getAdditionalPayrollPayTotals = (charges, payrollCurrency) => R.compose(
  R.filter(({ total }) => G.isNotZero(total)),
  R.map((currency) => ({
    currency,
    total: R.compose(
      G.mathRoundNumber,
      G.calculateChargesTotalExcludeDeductions,
      R.filter((charge) => R.or(
        R.propEq(currency, GC.FIELD_CURRENCY, charge),
        R.and(R.equals(currency, payrollCurrency), G.isNilOrEmpty(R.prop(GC.FIELD_CURRENCY, charge))),
      )),
    )(charges),
  })),
)(currencies);

const getExpensesTotalsByType = (expenses, isDeduction) => {
  const calculateFn = isDeduction ? G.calculateExpensesDeductionTotal : G.calculateExpensesReimbursementTotal;

  return R.compose(
    R.filter(({ total }) => G.isNotZero(total)),
    R.map((currency) => ({
      currency,
      [GC.FIELD_TOTAL]: R.compose(
        G.mathRoundNumber,
        calculateFn,
        R.filter(R.propEq(currency, GC.FIELD_CURRENCY)),
      )(expenses),
    })),
  )(currencies);
};

const getGrandTotals = (incomeSubtotals, deductionsTotals) => R.compose(
  R.filter(({ total }) => G.isNotZero(total)),
  R.map((currency) => {
    const income = R.find(R.propEq(currency, GC.FIELD_CURRENCY), incomeSubtotals);
    const deduction = R.find(R.propEq(currency, GC.FIELD_CURRENCY), deductionsTotals);
    const total = G.mathRoundNumber(R.subtract(
      R.pathOr(0, [GC.FIELD_TOTAL], income),
      R.pathOr(0, [GC.FIELD_TOTAL], deduction),
    ));

    return { total, currency };
  }),
)(currencies);

const getDriverPayrollIncomeSubtotals = (
  useMainCharges,
  mainChargesTotals,
  invoicesTotals,
  additionalPayrollPayTotals,
  expensesReimbursementTotals,
) => R.compose(
  R.filter(({ total }) => G.isNotZero(total)),
  R.map((currency) => {
    const arrayToUse = G.ifElse(useMainCharges, mainChargesTotals, invoicesTotals);

    const additionalPayrollPay = R.find(R.propEq(currency, GC.FIELD_CURRENCY), additionalPayrollPayTotals);
    const expensesReimbursement = R.find(R.propEq(currency, GC.FIELD_CURRENCY), expensesReimbursementTotals);

    const total = R.compose(
      G.mathRoundNumber,
      R.add(R.pathOr(0, [GC.FIELD_TOTAL], additionalPayrollPay)),
      R.add(R.pathOr(0, [GC.FIELD_TOTAL], expensesReimbursement)),
      R.pathOr(0, [GC.FIELD_TOTAL]),
      R.find(R.propEq(currency, GC.FIELD_CURRENCY)),
    )(arrayToUse);

    return { total, currency };
  }),
)(currencies);

const getDeductionsTotals = (totals) => R.compose(
  R.filter(({ total }) => G.isNotZero(total)),
  R.map((currency) => ({
    currency,
    total: R.compose(
      G.mathRoundNumber,
      R.sum,
      R.map(
        (entity) => {
          const exists = R.find(R.propEq(currency, GC.FIELD_CURRENCY), entity);

          return R.pathOr(0, [GC.FIELD_TOTAL], exists);
        },
      ),
    )(totals),
  })),
)(currencies);

const getAvailableCurrencies = R.compose(
  R.uniq,
  R.map(R.prop(GC.FIELD_CURRENCY)),
  R.flatten,
  R.values,
);

export const getDriverPayrollTotalsByCurrency = (props) => {
  const {
    values,
    expenses,
    fuelCards,
    tollCharges,
    useMainCharges,
    advancePayments,
    asyncInitialData,
  } = props;

  const payrollTotals = R.pathOr([], ['data', 'payrollTotals'], asyncInitialData);

  const payrollCurrency = R.pathOr(
    GC.DEFAULT_UI_CURRENCY,
    [GC.FIELD_CURRENCY],
    values,
  );

  const charges = R.pathOr([], [GC.FIELD_PAYROLL_CHARGES], values);
  const invoicesTotals = getInvoicesTotals(payrollTotals);
  const mainChargesTotals = getMainChargesTotals(payrollTotals);
  const fuelCardsTotals = getFuelCardsTotals(fuelCards);
  const tollChargesTotals = getTollChargesTotals(tollCharges);
  const advancedPaymentsTotal = G.calculateAdvancePaymentsTotal(advancePayments);
  const expensesDeductionTotals = getExpensesTotalsByType(expenses, true);

  const advancePaymentsTotals = G.ifElse(
    G.isZero(advancedPaymentsTotal),
    [],
    R.of(Array, {
      currency: payrollCurrency,
      total: advancedPaymentsTotal,
    }),
  );

  const expensesReimbursementTotals = getExpensesTotalsByType(expenses, false);
  const driverDeductionTotals = getDriverOrVendorDeductionTotals(charges, payrollCurrency);
  const additionalPayrollPayTotals = getAdditionalPayrollPayTotals(charges, payrollCurrency);

  const incomeSubtotals = getDriverPayrollIncomeSubtotals(
    useMainCharges,
    mainChargesTotals,
    invoicesTotals,
    additionalPayrollPayTotals,
    expensesReimbursementTotals,
  );

  const deductionsTotals = getDeductionsTotals([
    fuelCardsTotals,
    tollChargesTotals,
    advancePaymentsTotals,
    driverDeductionTotals,
    expensesDeductionTotals,
  ]);

  const grandTotals = getGrandTotals(incomeSubtotals, deductionsTotals);

  const currencyTotals = {
    grandTotals,
    invoicesTotals,
    fuelCardsTotals,
    incomeSubtotals,
    deductionsTotals,
    mainChargesTotals,
    tollChargesTotals,
    advancePaymentsTotals,
    driverDeductionTotals,
    expensesDeductionTotals,
    additionalPayrollPayTotals,
    expensesReimbursementTotals,
  };

  const payrollCurrencyTotals = {
    grandTotal: getSubTotal(grandTotals, payrollCurrency),
    invoicesTotal: getSubTotal(invoicesTotals, payrollCurrency),
    fuelCardsTotal: getSubTotal(fuelCardsTotals, payrollCurrency),
    incomeSubtotal: getSubTotal(incomeSubtotals, payrollCurrency),
    deductionsTotal: getSubTotal(deductionsTotals, payrollCurrency),
    mainChargesTotal: getSubTotal(mainChargesTotals, payrollCurrency),
    tollChargesTotal: getSubTotal(tollChargesTotals, payrollCurrency),
    advancePaymentsTotal: getSubTotal(advancePaymentsTotals, payrollCurrency),
    driverDeductionTotal: getSubTotal(driverDeductionTotals, payrollCurrency),
    expensesDeductionTotal: getSubTotal(expensesDeductionTotals, payrollCurrency),
    additionalPayrollPayTotal: getSubTotal(additionalPayrollPayTotals, payrollCurrency),
    expensesReimbursementTotal: getSubTotal(expensesReimbursementTotals, payrollCurrency),
  };

  const availableCurrencies = getAvailableCurrencies(currencyTotals);

  return {
    availableCurrencies,
    payrollCurrencyTotals,
    currencyTotals: R.map(R.indexBy(R.prop(GC.FIELD_CURRENCY)), currencyTotals),
  };
};

export const getDriverPayrollsTotals = (driverPayrolls) => R.compose(
  R.filter(({ total }) => G.isNotZero(total)),
  R.map((currency) => ({
    currency,
    total: R.compose(
      G.mathRoundNumber,
      R.sum,
      R.map(({ useMainCharges, invoiceTotal, mainChargeTotal, deductionChargesFromVendorTotal }) => G.ifElse(
        useMainCharges,
        R.add(R.or(mainChargeTotal, 0), R.or(deductionChargesFromVendorTotal, 0)),
        R.add(R.or(invoiceTotal, 0), R.or(deductionChargesFromVendorTotal, 0)),
      )),
      R.filter(R.propEq(currency, GC.FIELD_CURRENCY)),
      R.reduce((acc, totalCurrencies) => R.concat(acc, totalCurrencies), []),
      R.map(({ totalCurrencies, mainChargesTotal }) => {
        const useMainCharges = G.isNotNilAndNotZero(mainChargesTotal);

        return R.map(R.assoc('useMainCharges', useMainCharges), totalCurrencies);
      }),
      R.filter(R.prop('selected')),
    )(driverPayrolls),
  })),
)(currencies);

const getVendorPayrollIncomeSubtotals = (
    invoicesTotals,
    additionalPayrollPayTotals,
    expensesReimbursementTotals,
  ) => R.compose(
  R.filter(({ total }) => G.isNotZero(total)),
  R.map((currency) => {
    const additionalPayrollPay = R.find(R.propEq(currency, GC.FIELD_CURRENCY), additionalPayrollPayTotals);
    const expensesReimbursement = R.find(R.propEq(currency, GC.FIELD_CURRENCY), expensesReimbursementTotals);

    const total = R.compose(
      G.mathRoundNumber,
      R.add(R.pathOr(0, [GC.FIELD_TOTAL], additionalPayrollPay)),
      R.add(R.pathOr(0, [GC.FIELD_TOTAL], expensesReimbursement)),
      R.pathOr(0, [GC.FIELD_TOTAL]),
      R.find(R.propEq(currency, GC.FIELD_CURRENCY)),
    )(invoicesTotals);

    return { total, currency };
  }),
)(currencies);

export const getVendorPayrollTotalsByCurrency = (props) => {
  const {
    values,
    expenses,
    fuelCards,
    tollCharges,
    driverPayrolls,
    asyncInitialData,
  } = props;

  const payrollTotals = R.pathOr([], ['data', 'payrollTotals'], asyncInitialData);

  const payrollCurrency = R.pathOr(
    GC.DEFAULT_UI_CURRENCY,
    [GC.FIELD_CURRENCY],
    values,
  );

  const charges = R.pathOr([], [GC.FIELD_PAYROLL_CHARGES], values);
  const invoicesTotals = getInvoicesTotals(payrollTotals);
  const fuelCardsTotals = getFuelCardsTotals(fuelCards);
  const tollChargesTotals = getTollChargesTotals(tollCharges);
  const vendorDeductionTotals = getDriverOrVendorDeductionTotals(charges, payrollCurrency);
  const additionalPayrollPayTotals = getAdditionalPayrollPayTotals(charges, payrollCurrency);
  const expensesDeductionTotals = getExpensesTotalsByType(expenses, true);
  const expensesReimbursementTotals = getExpensesTotalsByType(expenses, false);

  const incomeSubtotals = getVendorPayrollIncomeSubtotals(
    invoicesTotals,
    additionalPayrollPayTotals,
    expensesReimbursementTotals,
  );

  const driverPayrollsTotals = getDriverPayrollsTotals(driverPayrolls);

  const deductionsTotals = getDeductionsTotals([
    fuelCardsTotals,
    tollChargesTotals,
    driverPayrollsTotals,
    vendorDeductionTotals,
    expensesDeductionTotals,
  ]);

  const grandTotals = getGrandTotals(incomeSubtotals, deductionsTotals);

  const currencyTotals = {
    grandTotals,
    invoicesTotals,
    fuelCardsTotals,
    incomeSubtotals,
    deductionsTotals,
    tollChargesTotals,
    driverPayrollsTotals,
    vendorDeductionTotals,
    expensesDeductionTotals,
    additionalPayrollPayTotals,
    expensesReimbursementTotals,
  };

  const payrollCurrencyTotals = {
    grandTotal: getSubTotal(grandTotals, payrollCurrency),
    invoicesTotal: getSubTotal(invoicesTotals, payrollCurrency),
    fuelCardsTotal: getSubTotal(fuelCardsTotals, payrollCurrency),
    incomeSubtotal: getSubTotal(incomeSubtotals, payrollCurrency),
    deductionsTotal: getSubTotal(deductionsTotals, payrollCurrency),
    tollChargesTotal: getSubTotal(tollChargesTotals, payrollCurrency),
    driverPayrollsTotal: getSubTotal(driverPayrollsTotals, payrollCurrency),
    vendorDeductionTotal: getSubTotal(vendorDeductionTotals, payrollCurrency),
    expensesDeductionTotal: getSubTotal(expensesDeductionTotals, payrollCurrency),
    additionalPayrollPayTotal: getSubTotal(additionalPayrollPayTotals, payrollCurrency),
    expensesReimbursementTotal: getSubTotal(expensesReimbursementTotals, payrollCurrency),
  };

  const availableCurrencies = getAvailableCurrencies(currencyTotals);

  return {
    availableCurrencies,
    payrollCurrencyTotals,
    currencyTotals: R.map(R.indexBy(R.prop(GC.FIELD_CURRENCY)), currencyTotals),
  };
};

export const getPayrollModalWithContent = (component) => ({
  p: '0',
  component,
  options: {
    top: 35,
    minWidth: 600,
    height: 'auto',
    maxWidth: '95vw',
    width: 'fit-content',
    backgroundColor: 'none',
  },
});

export const getUpdatePayrollInvoicesModalWithContent = (component) => ({
  p: '0',
  component,
  options: {
    top: 35,
    minWidth: 600,
    height: 'auto',
    maxWidth: '95vw',
    maxHeight: '95vh',
    width: 'fit-content',
    backgroundColor: 'none',
  },
});

export const getPayrollStatisticListAndCurrency = (resData) => {
  const { data, currency } = resData;

  let statisticList = [];

  if (G.isNotNilAndNotEmpty(data)) {
    statisticList = R.map(
      (item) => {
        const chargeFields = R.compose(
          R.fromPairs,
          R.map(([key, value]) => [`sumByAssessorial.${key}`, value]),
          R.toPairs,
          R.pathOr({}, [GC.FIELD_CHARGES]),
        )(item);

        return R.mergeRight(item, chargeFields);
      },
      data,
    );
  }

  return { statisticList, statisticCurrency: currency };
};

const getTotalFunctionMap = {
  fuelCards: getFuelCardsTotals,
  tollCharges: getTollChargesTotals,
  driverPayrolls: getDriverPayrollsTotals,
  deductions: getDriverOrVendorDeductionTotals,
  additionalPayrollPay: getAdditionalPayrollPayTotals,
  invoices: getInvoicesTotalsForVendorPayrollFiltering,
};

const getTotalsByDriverAndTruck = ({ guid, truckId, listType, entities, payrollCurrency}) => {
  const list = R.filter((entity) => {
    const { driverGuid, payedForTruck, primaryDriverGuid, payedForDriverGuid } = entity;

    const guidToUse = R.find(
      G.isNotNilAndNotEmpty,
      [driverGuid, primaryDriverGuid, payedForDriverGuid],
    );

    const id = R.find(
      G.isNotNilAndNotEmpty,
      [
        R.prop(GC.FIELD_FLEET_TRUCK_UNIT_ID, entity),
        R.prop(GC.GRC.TRUCK_UNIT_ID, entity),
        R.prop(GC.FIELD_UNIT_ID, payedForTruck),
      ],
    );

    const driverCondition = R.isNil(guid) ? G.isNilOrEmpty(guidToUse) : R.equals(guid, guidToUse);
    const truckCondition = R.isNil(truckId) ? G.isNilOrEmpty(id) : R.equals(truckId, id);

    return R.and(driverCondition, truckCondition);
  }, entities);

  return getSubTotal(getTotalFunctionMap[listType](list), payrollCurrency);
};

export const getDriversTrucksForFilters = ({
  charges,
  fuelCards,
  tollCharges,
  driverPayrolls,
  payrollInvoices,
  payrollCurrency,
}) => {
  const drivers = {};
  const trucks = [];

  [charges, fuelCards, tollCharges, driverPayrolls, payrollInvoices].flat().forEach((entity) => {
    const { driverGuid, payedForTruck, primaryDriverGuid, payedForDriverGuid } = entity;

    const guidToUse = R.find(
      G.isNotNilAndNotEmpty,
      [driverGuid, primaryDriverGuid, payedForDriverGuid],
    );

    const truckId = R.find(
      G.isNotNilAndNotEmpty,
      [
        R.prop(GC.FIELD_FLEET_TRUCK_UNIT_ID, entity),
        R.prop(GC.GRC.TRUCK_UNIT_ID, entity),
        R.prop(GC.FIELD_UNIT_ID, payedForTruck),
      ],
    );

    if (R.and(G.isNotNil(truckId), G.notContain(truckId, trucks))) trucks.push(truckId);

    if (R.and(G.isNotNil(guidToUse), R.isNil(R.prop(guidToUse, drivers)))) {
      const {
        payedForDriver,
        driverLastName,
        driverFirstName,
        primaryDriverLastName,
        primaryDriverFirstName,
      } = entity;

      const lastName = R.find(
        G.isNotNilAndNotEmpty,
        [
          driverLastName,
          primaryDriverLastName,
          R.prop(GC.GRC.DRIVER_LAST_NAME, entity),
          R.prop(GC.FIELD_LAST_NAME, payedForDriver),
        ],
      );

      const firstName = R.find(
        G.isNotNilAndNotEmpty,
        [
          driverFirstName,
          primaryDriverFirstName,
          R.prop(GC.GRC.DRIVER_FIRST_NAME, entity),
          R.prop(GC.FIELD_FIRST_NAME, payedForDriver),
        ],
      );

      drivers[guidToUse] = {
        lastName,
        firstName,
        driverGuid: guidToUse,
        trucks: G.isNotNil(truckId) ? [truckId] : [],
        fullName: `${R.or(firstName, '')} ${R.or(lastName, '')}`.trim(),
      };
    } else if (G.isNotNil(R.prop(guidToUse, drivers))) {
      if (R.and(G.isNotNil(truckId), G.notContain(truckId, R.path([guidToUse, 'trucks'], drivers)))) {
        drivers[guidToUse].trucks.push(truckId);
      }
    }
  });

  const indexedTrucks = R.compose(
    R.indexBy(R.prop('truckId')),
    R.map(
      (truckId) => {
        const noDriverData = {
          fuelCards: getTotalsByDriverAndTruck({
            truckId,
            payrollCurrency,
            entities: fuelCards,
            listType: 'fuelCards',
          }),
          tollCharges: getTotalsByDriverAndTruck({
            truckId,
            payrollCurrency,
            entities: tollCharges,
            listType: 'tollCharges',
          }),
          invoices: getTotalsByDriverAndTruck({
            truckId,
            payrollCurrency,
            listType: 'invoices',
            entities: payrollInvoices,
          }),
          driverPayrolls: getTotalsByDriverAndTruck({
            truckId,
            payrollCurrency,
            entities: driverPayrolls,
            listType: 'driverPayrolls',
          }),
          additionalPayrollPay: getTotalsByDriverAndTruck({
            truckId,
            payrollCurrency,
            entities: charges,
            listType: 'additionalPayrollPay',
          }),
          deductions: getTotalsByDriverAndTruck({
            truckId,
            payrollCurrency,
            entities: charges,
            listType: 'deductions',
          }),
        };

        const data = R.values(drivers).reduce((driversAcc, { driverGuid, primaryDriverGuid }) => {
          const guid = R.or(driverGuid, primaryDriverGuid);

          const totalsByDriver = {
            fuelCards: getTotalsByDriverAndTruck({
              guid,
              truckId,
              payrollCurrency,
              entities: fuelCards,
              listType: 'fuelCards',
            }),
            tollCharges: getTotalsByDriverAndTruck({
              guid,
              truckId,
              payrollCurrency,
              entities: tollCharges,
              listType: 'tollCharges',
            }),
            invoices: getTotalsByDriverAndTruck({
              guid,
              truckId,
              payrollCurrency,
              listType: 'invoices',
              entities: payrollInvoices,
            }),
          };

          return R.assoc(guid, totalsByDriver, driversAcc);
        }, {});

        return { truckId, filteredData: { ...data, noDriverData } };
      },
    ),
  )(trucks);

  const indexedDrivers = R.map(
    (driver) => {
      const { trucks, driverGuid } = driver;

      const noTruckData = {
        fuelCards: getTotalsByDriverAndTruck({
          payrollCurrency,
          guid: driverGuid,
          entities: fuelCards,
          listType: 'fuelCards',
        }),
        tollCharges: getTotalsByDriverAndTruck({
          payrollCurrency,
          guid: driverGuid,
          entities: tollCharges,
          listType: 'tollCharges',
        }),
        invoices: getTotalsByDriverAndTruck({
          payrollCurrency,
          guid: driverGuid,
          listType: 'invoices',
          entities: payrollInvoices,
        }),
        driverPayrolls: getTotalsByDriverAndTruck({
          payrollCurrency,
          guid: driverGuid,
          entities: driverPayrolls,
          listType: 'driverPayrolls',
        }),
        additionalPayrollPay: getTotalsByDriverAndTruck({
          payrollCurrency,
          guid: driverGuid,
          entities: charges,
          listType: 'additionalPayrollPay',
        }),
        deductions: getTotalsByDriverAndTruck({
          payrollCurrency,
          guid: driverGuid,
          entities: charges,
          listType: 'deductions',
        }),
      };

      const data = trucks.reduce((trucksAcc, truckId) => {
        const totalsByTruck = {
          fuelCards: getTotalsByDriverAndTruck({
            truckId,
            payrollCurrency,
            guid: driverGuid,
            entities: fuelCards,
            listType: 'fuelCards',
          }),
          tollCharges: getTotalsByDriverAndTruck({
            truckId,
            payrollCurrency,
            guid: driverGuid,
            entities: tollCharges,
            listType: 'tollCharges',
          }),
          invoices: getTotalsByDriverAndTruck({
            truckId,
            payrollCurrency,
            guid: driverGuid,
            listType: 'invoices',
            entities: payrollInvoices,
          }),
        };

        return R.assoc(truckId, totalsByTruck, trucksAcc);
      }, {});

      return R.assoc('filteredData', { ...data, noTruckData }, driver);
    },
    drivers,
  );

  return {
    indexedTrucks,
    indexedDrivers,
    trucks: R.map((value) => ({ value, label: value }), trucks),
    drivers: R.compose(
      R.map(({ fullName, driverGuid }) => ({
        label: fullName,
        value: driverGuid,
      })),
      R.values,
    )(drivers),
  };
};

const getFilteredEntities = (
  entities,
  { truckFilters, driverFilters },
  noTruck = false,
) => R.filter(
  (entity) => {
    const { driverGuid, payedForTruck, primaryDriverGuid, payedForDriverGuid } = entity;

    const guidToUse = R.find(
      G.isNotNilAndNotEmpty,
      [driverGuid, primaryDriverGuid, payedForDriverGuid],
    );

    const truckId = R.find(
      G.isNotNilAndNotEmpty,
      [
        R.prop(GC.FIELD_FLEET_TRUCK_UNIT_ID, entity),
        R.prop(GC.GRC.TRUCK_UNIT_ID, entity),
        R.prop(GC.FIELD_UNIT_ID, payedForTruck),
      ],
    );

    const driverMatch = R.includes(guidToUse, driverFilters);
    const truckMatch = R.includes(truckId, truckFilters);

    if (R.or(noTruck, G.isNilOrEmpty(truckFilters))) return driverMatch;

    if (G.isNilOrEmpty(driverFilters)) return truckMatch;

    return R.and(driverMatch, truckMatch);
  },
  entities,
);

export const getFilteredVendorPayrollData = ({
  filters,
  charges,
  fuelCards,
  tollCharges,
  driverPayrolls,
  payrollInvoices,
}) => {
  if (R.isNil(filters)) return { fuelCards, tollCharges, driverPayrolls, payrollInvoices };

  const { truckFilters, driverFilters } = filters;

  if (R.and(G.isNilOrEmpty(driverFilters), G.isNilOrEmpty(truckFilters))) {
    return { fuelCards, tollCharges, driverPayrolls, payrollInvoices };
  }

  return {
    charges: getFilteredEntities(charges, filters),
    fuelCards: getFilteredEntities(fuelCards, filters),
    tollCharges: getFilteredEntities(tollCharges, filters),
    payrollInvoices: getFilteredEntities(payrollInvoices, filters),
    driverPayrolls: getFilteredEntities(driverPayrolls, filters, true),
  };
};

const getDriverOrTruckSubTotals = (data, filters) => {
  const dataToMap = G.isNilOrEmpty(filters) ? data : R.pick(filters, data);

  const getTotal = (propName) => R.compose(
    G.mathRoundNumber,
    R.sum,
    R.map(R.propOr(0, propName)),
    R.values,
  )(dataToMap);

  return {
    deductions: getTotal('deductions'),
    invoicesTotal: getTotal('invoices'),
    fuelCardsTotal: getTotal('fuelCards'),
    tollChargesTotal: getTotal('tollCharges'),
    driverPayrollsTotal: getTotal('driverPayrolls'),
    additionalPayrollPay: getTotal('additionalPayrollPay'),
  };
};

export const getDriversSubTotals = ({ driverFilters, truckFilters, filtersData }) => driverFilters.reduce(
  (acc, guid) => R.assoc(
    guid,
    getDriverOrTruckSubTotals(R.path(['indexedDrivers', guid, 'filteredData'], filtersData), truckFilters),
    acc,
  ),
  {},
);

export const getTrucksSubTotals = ({ driverFilters, truckFilters, filtersData }) => truckFilters.reduce(
  (acc, truckId) => R.assoc(
    truckId,
    getDriverOrTruckSubTotals(R.path(['indexedTrucks', truckId, 'filteredData'], filtersData), driverFilters),
    acc,
  ),
  {},
);
